import React, { useState, useEffect, useRef } from 'react'
import { Cancel, CheckCircle, ReportProblem, Error as ErrorIcon, ZoomIn, ZoomOut, Fullscreen, FullscreenExit, SchoolOutlined } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download'
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs'
import { CurrentRequirementsSchema } from '../data/types'
import { getChangedAttributes, getCredentialExpirationInfo } from '../utils/helpers'

const MEMBERSHIP_AGREEMENT_REQUIREMENT_ID = 'membership-agreement';
const ORIENTATION_TRAINING_CATEGORY = 'orientation_training';

const isOrientationTrainingCredential = (credential: CurrentRequirementsSchema): boolean =>
  credential.category === ORIENTATION_TRAINING_CATEGORY;

const detectPreviewFileType = (url: string, credential: CurrentRequirementsSchema): 'pdf' | 'image' => {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.pdf') || credential.id === MEMBERSHIP_AGREEMENT_REQUIREMENT_ID || credential.code === 'MEMBER_AGREEMENT') {
    return 'pdf';
  }
  if (/\.(jpe?g|png|gif|webp|svg)$/.test(path)) {
    return 'image';
  }
  return 'image';
};

// Add new props for actions
interface CredentialPreviewProps {
  credential: CurrentRequirementsSchema
  handleClose: () => void
  onApprove: (credentialId: string, updatedCredentialDetails: CurrentRequirementsSchema) => void
  onReject: (credentialId: string) => void
  onCancel: () => void
  onCredentialDetailsChange?: (details: { expiration: string; credential_number: string; municipality: string }) => void
}

const CredentialPreview: React.FC<CredentialPreviewProps> = ({ credential, handleClose, onApprove, onReject, onCancel, onCredentialDetailsChange }) => {
  const [fileType, setFileType] = useState<string | null>(null)
  const [pdfNumPages, setPdfNumPages] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [expiration, setExpiration] = useState(credential.expiration || '')
  const [credentialNumber, setCredentialNumber] = useState(credential.credential_number || '')
  const [municipality, setMunicipality] = useState(credential.municipality || '')
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)
  const pdfCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
  const isMembershipAgreement =
    credential.id === MEMBERSHIP_AGREEMENT_REQUIREMENT_ID || credential.code === 'MEMBER_AGREEMENT';
  const isOrientationTraining = isOrientationTrainingCredential(credential);
  const isPayment = credential.code === 'PAY';
  const isCredentialReview =
    !isMembershipAgreement && !isOrientationTraining && !isPayment;
  const expirationInfo =
    credential.is_met && isCredentialReview
      ? getCredentialExpirationInfo(credential.expiration)
      : null;
  const approvedHeaderIcon =
    expirationInfo?.status === 'expired'
      ? { Icon: ErrorIcon, color: 'error' as const }
      : expirationInfo?.status === 'expiring_soon'
        ? { Icon: ReportProblem, color: 'warning' as const }
        : { Icon: CheckCircle, color: 'success' as const };
  const HeaderStatusIcon = approvedHeaderIcon.Icon;
  const approvedDetailsBannerClass =
    expirationInfo?.status === 'expired'
      ? 'bg-red-50 border-red-200'
      : expirationInfo?.status === 'expiring_soon'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-green-50 border-green-200';
  const approvedDetailsLabelClass =
    expirationInfo?.status === 'expired'
      ? 'text-red-800'
      : expirationInfo?.status === 'expiring_soon'
        ? 'text-amber-800'
        : 'text-green-800';
  const approvedDetailsValueClass =
    expirationInfo?.status === 'expired'
      ? 'text-red-900'
      : expirationInfo?.status === 'expiring_soon'
        ? 'text-amber-900'
        : 'text-green-900';

  useEffect(() => {
    let cancelled = false

    const loadPreview = async () => {
      setIsLoading(true)
      setError(null)
      setPdfNumPages(0)
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy()
        pdfDocRef.current = null
      }

      if (!credential.presigned_url) {
        setError('No file URL provided')
        setIsLoading(false)
        return
      }
      if (!/^https?:\/\//i.test(credential.presigned_url.trim())) {
        setError('Document URL is invalid. The file link must be a full https URL.')
        setIsLoading(false)
        return
      }

      try {
        const resolvedType = detectPreviewFileType(credential.presigned_url, credential)
        setFileType(resolvedType)

        if (resolvedType === 'pdf') {
          try {
            const response = await fetch(credential.presigned_url)
            if (!response.ok) {
              throw new Error(`Failed to fetch PDF (${response.status})`)
            }
            const arrayBuffer = await response.arrayBuffer()
            if (cancelled) return

            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
            if (cancelled) {
              pdf.destroy()
              return
            }

            pdfDocRef.current = pdf
            setPdfNumPages(pdf.numPages)
          } catch (pdfErr) {
            if (cancelled) return
            const message = pdfErr instanceof Error ? pdfErr.message : 'Failed to load PDF'
            setError(message)
          }
        }
      } catch (err) {
        setError('Unable to determine file type')
        console.error('Error detecting file type:', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadPreview()

    return () => {
      cancelled = true
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy()
        pdfDocRef.current = null
      }
    }
  }, [credential])

  useEffect(() => {
    const pdf = pdfDocRef.current
    if (!pdf || pdfNumPages === 0) return

    let cancelled = false

    const renderPages = async () => {
      for (let pageNum = 1; pageNum <= pdfNumPages; pageNum++) {
        if (cancelled) return
        const canvas = pdfCanvasRefs.current[pageNum - 1]
        if (!canvas) continue

        try {
          const page = await pdf.getPage(pageNum)
          const viewport = page.getViewport({ scale: zoom })
          const context = canvas.getContext('2d')
          if (!context) continue

          canvas.height = viewport.height
          canvas.width = viewport.width
          await page.render({ canvasContext: context, viewport, canvas }).promise
        } catch (renderErr) {
          if (cancelled) return
          const message = renderErr instanceof Error ? renderErr.message : 'Failed to render PDF page'
          setError(message)
        }
      }
    }

    renderPages()

    return () => {
      cancelled = true
    }
  }, [pdfNumPages, zoom])

  const handleDownload = () => {
    if (credential.presigned_url) {
      const link = document.createElement('a')
      link.href = credential.presigned_url
      link.download = credential.name || 'credential'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25))
  }

  const handleResetZoom = () => {
    setZoom(1)
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }


  const handleOnApprove = () => {
    const updatedCredentialDetails = {
      ...credential,
      municipality: municipality,
      credential_number: credentialNumber,
      expiration: expiration,
      is_met: true
    }

    const changes = getChangedAttributes(credential, updatedCredentialDetails)
    
    if (Object.keys(changes).length > 0) {
      onApprove(credential?.id || '', changes)
    }
  }

  const handleOrientationMarkIncomplete = () => {
    if (!credential.is_met) return
    onReject(credential?.id || '')
  }

  // const renderOrientationTrainingStatusBanner = () => {
  //   if (!isOrientationTraining) return null

  //   if (credential.is_met) {
  //     return (
  //       <div
  //         className="bg-[#EFF8FA] border border-[#B8E4F0] rounded-lg p-4 mb-4"
  //         data-testid="orientation-training-status-banner"
  //       >
  //         <div className="flex items-start gap-3">
  //           <CheckCircle color="success" sx={{ fontSize: 22 }} />
  //           <div>
  //             <p className="text-sm font-raleway font-semibold text-[#0A7A32]">Orientation or training completed</p>
  //             <p className="text-sm font-open-sans text-[#4D4D4D] mt-1">
  //               This member has fulfilled the orientation or training requirement. You can reopen it below or mark it incomplete if needed.
  //             </p>
  //           </div>
  //         </div>
  //       </div>
  //     )
  //   }

  //   if (credential.presigned_url) {
  //     return (
  //       <div
  //         className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
  //         data-testid="orientation-training-status-banner"
  //       >
  //         <div className="flex items-start gap-3">
  //           <ReportProblem color="warning" sx={{ fontSize: 22 }} />
  //           <div>
  //             <p className="text-sm font-raleway font-semibold text-amber-800">Pending review</p>
  //             <p className="text-sm font-open-sans text-amber-700 mt-1">
  //               Review the completion record below, then mark this orientation or training as complete if the member has satisfied the requirement.
  //             </p>
  //           </div>
  //         </div>
  //       </div>
  //     )
  //   }

  //   return (
  //     <div
  //       className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
  //       data-testid="orientation-training-status-banner"
  //     >
  //       <div className="flex items-start gap-3">
  //         <ErrorIcon color="error" sx={{ fontSize: 22 }} />
  //         <div>
  //           <p className="text-sm font-raleway font-semibold text-red-700">Not completed</p>
  //           <p className="text-sm font-open-sans text-red-600 mt-1">
  //             No completion record has been uploaded yet. You can still mark this requirement complete manually if the member finished orientation or training offline.
  //           </p>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  const handleCredentialDetailsChange = (field: 'expiration' | 'credential_number' | 'municipality', value: string) => {
    switch (field) {
      case 'expiration':
        setExpiration(value)
        break
      case 'credential_number':
        setCredentialNumber(value)
        break
      case 'municipality':
        setMunicipality(value)
        break
    }
    
    // Call the callback with updated values
    if (onCredentialDetailsChange) {
      onCredentialDetailsChange({
        expiration: field === 'expiration' ? value : expiration,
        credential_number: field === 'credential_number' ? value : credentialNumber,
        municipality: field === 'municipality' ? value : municipality
      })
    }
  }

  const renderZoomControls = () => {
    if (fileType !== 'image' && fileType !== 'pdf') return null

    return (
      <div className="absolute top-3 left-3 flex gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 0.25}
          className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom Out"
        >
          <ZoomOut className="text-gray-600" fontSize="small" />
        </button>
        <button
          onClick={handleResetZoom}
          className="px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 rounded"
          title="Reset Zoom"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom In"
        >
          <ZoomIn className="text-gray-600" fontSize="small" />
        </button>
        <button
          onClick={toggleFullscreen}
          className="p-1 rounded hover:bg-gray-200"
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? <FullscreenExit className="text-gray-600" fontSize="small" /> : <Fullscreen className="text-gray-600" fontSize="small" />}
        </button>
      </div>
    )
  }

  const renderOrientationTrainingFileContent = () => (
    <div
      className="flex flex-col items-center justify-center p-12 text-center"
      data-testid="orientation-training-file-content"
    >
      <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${
        credential.is_met ? 'bg-[#EFF8FA]' : 'bg-red-100'
      }`}>
        <SchoolOutlined className={`${credential.is_met ? 'text-primary-300' : 'text-red-500'}`} sx={{ fontSize: 48 }} />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">
        {credential.is_met ? 'Completion Record on File' : 'No Completion Record'}
      </h3>
      <p className="text-gray-600 max-w-sm">
        {credential.is_met
          ? 'This orientation or training requirement has been completed.'
          : 'This orientation or training requirement is still outstanding. No completion record has been added yet.'}
      </p>
    </div>
  )

  const renderFileContent = () => {
    if (isOrientationTraining && (!credential.presigned_url || error)) {
      return renderOrientationTrainingFileContent()
    }

    // Show not submitted state when no presigned URL
    if (!credential.presigned_url) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ErrorIcon className="text-red-500" sx={{ fontSize: 48 }} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Document Submitted</h3>
          <p className="text-gray-600 max-w-sm">
            {credential.is_required
              ? "This credential is required but no document has been uploaded yet."
              : "No document has been uploaded for this credential."}
          </p>
          {credential.is_required && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                This credential is required for compliance
              </p>
            </div>
          )}
        </div>
      )
    }
    
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ErrorIcon className="text-red-500" sx={{ fontSize: 32 }} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Failed to Load Document</h3>
          <p className="text-red-600">{error}</p>
        </div>
      )
    }
    if (fileType === 'image') {
      return (
        <div className="overflow-auto max-h-96">
          <img 
            src={credential.presigned_url} 
            alt={credential.name || 'Credential document'}
            className={`object-contain rounded-lg shadow-md mx-auto transition-transform duration-200 ${isFullscreen ? 'max-w-none' : 'max-w-full'}`}
            style={{ 
              transform: `scale(${zoom})`,
              transformOrigin: 'center center'
            }}
            onError={() => setError('Failed to load image')}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      )
    }
    if (fileType === 'pdf' && pdfNumPages > 0) {
      return (
        <div
          className={`w-full overflow-y-auto rounded-lg border border-[#E2E8F0] bg-white ${isFullscreen ? 'h-[85vh]' : 'h-[70vh]'} min-h-[480px] p-4`}
          data-testid="pdf-preview-container"
        >
          {Array.from({ length: pdfNumPages }, (_, index) => (
            <canvas
              key={index}
              ref={(el) => { pdfCanvasRefs.current[index] = el }}
              data-testid={index === 0 ? 'pdf-preview-canvas' : undefined}
              aria-label={`${credential.name || 'PDF document'} page ${index + 1}`}
              className="mx-auto mb-4 max-w-full shadow-md"
            />
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'w-full max-w-2xl mx-auto'} rounded-xl p-6`}>
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      {/* Title and subtitle */}
      <div className="mb-2">
        <div className="flex flex-row items-center">
          {
            (credential.is_met) ? (
              <HeaderStatusIcon color={approvedHeaderIcon.color} className="my-auto mr-1" sx={{ fontSize: 30 }} />
            ) : (
              (credential?.is_required && credential?.presigned_url) ? (
                <ReportProblem color="warning" className="my-auto mr-1" sx={{ fontSize: 30 }} />
              ) : (
                  <ErrorIcon color="error" className="my-auto mr-1" />
              )
            )
          }
          <p className='my-auto text-[20px] font-semibold text-black font-raleway'>{credential.name || 'Credential'}</p>
          {isOrientationTraining && (
            <span className="ml-2 rounded-full bg-[#EFF8FA] px-2 py-0.5 text-[11px] font-open-sans font-medium text-primary-200">
              Orientation / Training
            </span>
          )}
          {isPayment && (
            <span className="ml-2 rounded-full bg-[#FFF4E5] px-2 py-0.5 text-[11px] font-open-sans font-medium text-[#B8860B]">
              Payment
            </span>
          )}
        </div>
        <div className="text-[16px] text-[#4D4D4D]/70 mt-1 font-open-sans">
          {isMembershipAgreement
            ? "View the member's signed membership agreement."
            : isPayment
              ? credential.is_met
                ? 'This member has a valid payment method on file for their membership.'
                : 'This member still needs to add a payment method to complete membership billing setup.'
              : isOrientationTraining
                ? credential.is_met
                  ? 'This orientation or training requirement has been completed.'
                  : credential.presigned_url
                    ? 'Review the submitted completion record and confirm the member has fulfilled this orientation or training requirement.'
                    : 'This orientation or training has not been completed yet.'
                : credential.is_met 
                  ? expirationInfo?.status === 'expired'
                    ? 'This credential has expired and needs to be renewed.'
                    : expirationInfo?.status === 'expiring_soon'
                      ? 'This credential is approved but expires within the next 30 days.'
                      : "This credential has been approved and is currently valid."
                  : credential.presigned_url 
                    ? "Review the submitted file and approve if it's clear and meets the requirements, or reject if it doesn't."
                    : credential.is_required
                      ? "This credential is required but no document has been submitted for review."
                      : "No document has been submitted for this credential."
          }
        </div>
      </div>

      {/* {renderOrientationTrainingStatusBanner()} */}
      
      {/* Credential details - shown when credential is met */}
      {(credential.is_met && isCredentialReview) && (
        <div
          className={`${approvedDetailsBannerClass} border rounded-lg p-4 mb-4`}
          data-testid={
            expirationInfo?.status === 'expired'
              ? 'credential-expired-banner'
              : expirationInfo?.status === 'expiring_soon'
                ? 'credential-expiring-soon-banner'
                : 'credential-valid-banner'
          }
        >
          {expirationInfo?.status === 'expiring_soon' && (
            <div className="flex items-start gap-2 mb-3">
              <ReportProblem color="warning" sx={{ fontSize: 20 }} />
              <p className={`text-sm font-open-sans ${approvedDetailsLabelClass}`}>
                {expirationInfo.detailLabel}
              </p>
            </div>
          )}
          {expirationInfo?.status === 'expired' && (
            <div className="flex items-start gap-2 mb-3">
              <ErrorIcon color="error" sx={{ fontSize: 20 }} />
              <p className={`text-sm font-open-sans ${approvedDetailsLabelClass}`}>
                {expirationInfo.detailLabel}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {credential.credential_number && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${approvedDetailsLabelClass} mb-1`}>Credential Number</span>
                <span className={`text-base ${approvedDetailsValueClass} font-mono`}>{credential.credential_number}</span>
              </div>
            )}
            {credential.municipality && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${approvedDetailsLabelClass} mb-1`}>Municipality</span>
                <span className={`text-base ${approvedDetailsValueClass}`}>{credential.municipality}</span>
              </div>
            )}
            {credential.expiration && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${approvedDetailsLabelClass} mb-1`}>Expiration Date</span>
                <span
                  className={`text-base ${approvedDetailsValueClass}`}
                  data-testid="credential-expiration-date"
                >
                  {expirationInfo?.formattedDate ?? new Date(credential.expiration).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Credential details input - shown when credential needs review */}
      {isCredentialReview && !credential.is_met && credential.is_required && credential.presigned_url && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="mb-3">
            {/* <h4 className="text-sm font-semibold text-amber-800 mb-1">Required Credential Details</h4> */}
            <p className="text-sm text-amber-700 font-open-sans font-light">Please provide the following details to approve required credential</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="text-xs font-raleway font-medium text-amber-800 mb-1">
                Credential Number 
                {/* <span className="text-red-500">*</span> */}
              </label>
              <input
                type="text"
                value={credentialNumber}
                onChange={(e) => handleCredentialDetailsChange('credential_number', e.target.value)}
                className="px-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs"
                placeholder="Enter credential number"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-raleway font-medium text-amber-800 mb-1">
                Municipality 
                {/* <span className="text-red-500">*</span> */}
              </label>
              <input
                type="text"
                value={municipality}
                onChange={(e) => handleCredentialDetailsChange('municipality', e.target.value)}
                className="px-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs"
                placeholder="Enter municipality"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-raleway font-medium text-amber-800 mb-1">
                Expiration Date 
                {/* <span className="text-red-500">*</span> */}
              </label>
              <input
                type="date"
                value={expiration}
                onChange={(e) => handleCredentialDetailsChange('expiration', e.target.value)}
                className="px-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Card preview */}
      {!isPayment && (
          <div className={`rounded-lg shadow border p-4 mt-4 mb-6 relative flex flex-col ${
            (isOrientationTraining && credential.is_met) ? 'bg-[#FAFEFF] border-[#B8E4F0]' : 'bg-[#FAFAFA]'
          }`}>
            {/* {isOrientationTraining && (
              <div className="mb-3 flex items-center gap-2">
                <SchoolOutlined className="text-primary-300" sx={{ fontSize: 18 }} />
                <p className="text-sm font-raleway font-semibold text-[#333333]">
                  {credential.is_met ? 'Completion record on file' : 'Completion record preview'}
                </p>
              </div>
            )} */}
            {/* Zoom controls */}
            {credential.presigned_url && renderZoomControls()}
            {credential.presigned_url && (
              <button
                className="absolute top-3 right-3 bg-gray-100 hover:bg-gray-200 rounded-full p-2 shadow z-10"
                onClick={handleDownload}
                aria-label="Download file"
              >
                <DownloadIcon className="text-gray-600" />
              </button>
            )}
            {/* Certificate preview */}
            <div className="flex justify-center items-center min-h-[200px]">
              {renderFileContent()}
            </div>
          </div>
        )
      }
      {/* Action buttons */}
      <div className="flex items-center justify-between mt-2 font-raleway">
        <button
          className="px-4 py-2 text-[16px] rounded border-0 text-gray-700 bg-white hover:bg-gray-100 font-medium"
          onClick={onCancel}
        >
          Back
        </button>
        <div className="flex gap-3">
          {isOrientationTraining && !isMembershipAgreement && !isPayment && credential?.id && (
            credential.is_met ? (
              <button
                type="button"
                className="px-4 py-2 text-[16px] rounded bg-white border border-[#E9E9E9]/90 text-red-600 font-semibold hover:bg-red-50"
                onClick={handleOrientationMarkIncomplete}
                data-testid="orientation-mark-incomplete-button"
              >
                Mark Incomplete
              </button>
            ) : (
              <button
                type="button"
                className="px-4 py-2 text-[16px] rounded bg-primary-200 text-white font-semibold hover:bg-primary-500 shadow"
                onClick={handleOnApprove}
                data-testid="orientation-mark-complete-button"
              >
                Mark Complete
              </button>
            )
          )}
          {
            isCredentialReview && (credential?.presigned_url || credential?.is_met) ? (
              <button
                type="button"
                className="px-4 py-2 text-[16px] rounded bg-white border border-[#E9E9E9]/90 text-red-600 font-semibold hover:bg-red-50"
                onClick={() => onReject(credential?.id || '')}
                >
                Reject
              </button>
            ) : null
          }
          {
            isCredentialReview && !credential.is_met && credential?.presigned_url ? (
              <button
                type="button"
                disabled={!credentialNumber || !municipality}
                className={`px-4 py-2 text-[16px] rounded bg-primary-200 text-white font-semibold hover:bg-primary-500 shadow ${(!credentialNumber || !municipality) ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleOnApprove}
              >
                Approve
              </button>
            ) : null
          }
        </div>
      </div>
    </div>
  )
}

export default CredentialPreview
